import { useState, useEffect, useCallback, useRef } from 'react';
import { validateSMSSettings } from '../utils/validation';
import { mergeSMSSettingsWithDefaults } from '../constants/defaultTemplates';
import { useTutorial } from '../../../components/tutorial/TutorialContext';
import { smsSettingsService } from '../services/smsSettingsService';

const CACHE_PREFIX = 'sms-settings-cache';
const SAVE_DELAY_MS = 350;

const getCacheKey = (companyId) => `${CACHE_PREFIX}:${companyId}`;

const readCachedSettings = (companyId) => {
  if (!companyId || typeof window === 'undefined') {
    return null;
  }

  try {
    const cachedValue = window.localStorage.getItem(getCacheKey(companyId));
    return cachedValue ? mergeSMSSettingsWithDefaults(JSON.parse(cachedValue)) : null;
  } catch (error) {
    console.warn('Unable to read cached SMS settings:', error);
    return null;
  }
};

const writeCachedSettings = (companyId, settings) => {
  if (!companyId || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(getCacheKey(companyId), JSON.stringify(settings));
  } catch (error) {
    console.warn('Unable to cache SMS settings:', error);
  }
};

const useSMSSettings = (companyId) => {
  const { markStepComplete } = useTutorial();
  const [settings, setSettings] = useState(() => readCachedSettings(companyId));
  const [loading, setLoading] = useState(() => Boolean(companyId) && !readCachedSettings(companyId));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const getSMSSettingsCall = useCallback(
    (payload) => smsSettingsService.getSMSSettings(payload.companyId),
    []
  );
  const updateSMSSettingsCall = useCallback(
    (payload) => smsSettingsService.updateSMSSettings(payload.companyId, payload.settings),
    []
  );
  const sendTestSMSCall = useCallback(
    (payload) =>
      smsSettingsService.sendTestSMS(
        payload.companyId,
        payload.templateType,
        payload.phoneNumber,
        payload.customMessage
      ),
    []
  );
  const isMountedRef = useRef(true);
  const settingsRef = useRef(settings);
  const pendingSaveRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, []);

  const flushQueuedSaves = useCallback(async () => {
    if (!companyId || saveInFlightRef.current || !pendingSaveRef.current) {
      return;
    }

    saveInFlightRef.current = true;

    while (pendingSaveRef.current) {
      const nextSettings = pendingSaveRef.current;
      pendingSaveRef.current = null;

      try {
        await updateSMSSettingsCall({
          companyId,
          settings: nextSettings
        });

        if (
          nextSettings?.reminderEnabled &&
          Array.isArray(nextSettings?.reminders) &&
          nextSettings.reminders.length > 0
        ) {
          markStepComplete(6);
        }

        if (isMountedRef.current) {
          setError(null);
        }
      } catch (err) {
        console.error('Error updating SMS settings:', err);

        if (isMountedRef.current) {
          setError('Failed to save SMS settings. Changes will keep trying as you continue updating.');
        }
      }
    }

    saveInFlightRef.current = false;

    if (isMountedRef.current) {
      setSaving(false);
    }
  }, [companyId, markStepComplete, updateSMSSettingsCall]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void flushQueuedSaves();
    }, SAVE_DELAY_MS);
  }, [flushQueuedSaves]);

  const fetchSettings = useCallback(async ({ background = false } = {}) => {
    if (!companyId) return;

    try {
      if (!background && !settingsRef.current) {
        setLoading(true);
      }

      const result = await getSMSSettingsCall({ companyId });
      const hasPendingLocalChanges = Boolean(pendingSaveRef.current) || saveInFlightRef.current;

      if (isMountedRef.current && !hasPendingLocalChanges) {
        const normalized = mergeSMSSettingsWithDefaults(result);
        writeCachedSettings(companyId, normalized);
        setSettings(normalized);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching SMS settings:', err);

      if (isMountedRef.current && !settingsRef.current) {
        setError('Failed to load SMS settings. Please try again later.');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [companyId, getSMSSettingsCall]);

  useEffect(() => {
    if (!companyId) {
      pendingSaveRef.current = null;
      setSettings(null);
      setLoading(false);
      setError(null);
      return;
    }

    const cachedSettings = readCachedSettings(companyId);

    pendingSaveRef.current = null;

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    settingsRef.current = cachedSettings;
    setSettings(cachedSettings);
    setLoading(!cachedSettings);
    setError(null);

    void fetchSettings({ background: Boolean(cachedSettings) });
  }, [companyId, fetchSettings]);

  const handleUpdateSettings = async (newSettings) => {
    if (!companyId) return false;

    const validationErrors = validateSMSSettings(newSettings);
    if (Object.keys(validationErrors).length > 0) {
      setError('Invalid settings. Please check your input and try again.');
      return false;
    }

    settingsRef.current = newSettings;
    pendingSaveRef.current = newSettings;
    writeCachedSettings(companyId, newSettings);

    if (isMountedRef.current) {
      setSettings(newSettings);
      setError(null);
      setSaving(true);
    }

    scheduleSave();
    return true;
  };

  const handleSendTestSMS = async (templateType, phoneNumber, customMessage) => {
    if (!companyId) return;

    try {
      setError(null);
      await sendTestSMSCall({
        companyId,
        templateType,
        phoneNumber,
        settings: settingsRef.current, // Pass current settings for template rendering
        ...(customMessage ? { customMessage } : {})
      });
      return true;
    } catch (err) {
      console.error('Error sending test SMS:', err);
      const message =
        err?.message ||
        err?.details ||
        'Failed to send test SMS. Please try again later.';
      setError(message);
      return false;
    }
  };

  return {
    settings,
    loading,
    error,
    saving,
    updateSettings: handleUpdateSettings,
    sendTestSMS: handleSendTestSMS,
    refreshSettings: fetchSettings
  };
};

export default useSMSSettings; 